import { useState, useEffect, useRef, Children } from 'react';
import { motion, AnimatePresence, useAnimation } from 'framer-motion';
import PropTypes from 'prop-types';

/**
 * SwipeablePanels - A framer-motion based replacement for react-swipeable-views
 * Provides animated panel transitions with optional swipe gesture support
 */
export function SwipeablePanels({
  children,
  index = 0,
  onChangeIndex,
  axis = 'x',
  style,
  className,
  animationDuration = 0.3,
  enableSwipe = false,
}) {
  const isRtl = axis === 'x-reverse';
  const direction = isRtl ? -1 : 1;
  const containerRef = useRef(null);
  const controls = useAnimation();
  const [dragStart, setDragStart] = useState(0);

  const childArray = Children.toArray(children);

  useEffect(() => {
    controls.start({ x: -index * 100 * direction + '%' });
  }, [index, controls, direction]);

  const handleDragStart = (_, info) => {
    setDragStart(info.point.x);
  };

  const handleDragEnd = (_, info) => {
    if (!enableSwipe || !onChangeIndex) return;

    const swipeThreshold = 50;
    const diff = info.point.x - dragStart;

    if (Math.abs(diff) > swipeThreshold) {
      if (diff > 0 && index > 0) {
        onChangeIndex(index - 1);
      } else if (diff < 0 && index < childArray.length - 1) {
        onChangeIndex(index + 1);
      }
    }
  };

  const variants = {
    enter: direction => ({
      x: direction > 0 ? '100%' : '-100%',
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: direction => ({
      x: direction < 0 ? '100%' : '-100%',
      opacity: 0,
    }),
  };

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        overflow: 'hidden',
        position: 'relative',
        ...style,
      }}
    >
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={index}
          custom={direction}
          variants={variants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{
            x: { type: 'spring', stiffness: 300, damping: 30 },
            opacity: { duration: animationDuration },
          }}
          drag={enableSwipe ? 'x' : false}
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={0.1}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          style={{ width: '100%' }}
        >
          {childArray[index]}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

SwipeablePanels.propTypes = {
  children: PropTypes.node.isRequired,
  index: PropTypes.number,
  onChangeIndex: PropTypes.func,
  axis: PropTypes.oneOf(['x', 'x-reverse']),
  style: PropTypes.object,
  className: PropTypes.string,
  animationDuration: PropTypes.number,
  enableSwipe: PropTypes.bool,
};

/**
 * SwipeablePanel - Panel wrapper component for SwipeablePanels
 * Conditionally renders children based on active index
 */
export function SwipeablePanel({ children, value, index, dir, ...other }) {
  const isActive = value === index;

  return (
    <div
      role="tabpanel"
      hidden={!isActive}
      id={`swipeable-tabpanel-${index}`}
      aria-labelledby={`swipeable-tab-${index}`}
      dir={dir}
      {...other}
    >
      {isActive && children}
    </div>
  );
}

SwipeablePanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
  dir: PropTypes.string,
};

export default SwipeablePanels;
